import type { FilterCapabilityConfig } from '@omnea-app/packages/backend/filtering/types/baseFilterTypes'

// Filter/sort field definitions shared by the FE (column capabilities) and BE (Kysely config).
// Must not import Kysely/DB types — the frontend imports this.
const REQUESTS_FILTER_FIELDS = {
  filterableFields: {
    name: { type: 'scalar', column: 'requests.name', scalarType: 'string' },
    publicId: { type: 'scalar', column: 'requests.public_id', scalarType: 'string' },
    createdAt: { type: 'scalar', column: 'requests.created_at', scalarType: 'timestamp' },
    completedAt: { type: 'scalar', column: 'requests.completed_at', scalarType: 'timestamp' },
    priority: { type: 'scalar', column: 'requests.priority', scalarType: 'enum' },
    // enumMapping bridges the FE's status options to the generated `requests.status` column,
    // which stores `inProgress` where the UI (and `deriveRequestStatus`) show `pending`.
    status: {
      type: 'scalar',
      column: 'requests.status',
      scalarType: 'enum',
      enumMapping: { pending: 'inProgress', completed: 'completed', rejected: 'rejected', paused: 'paused' },
    },
    // Relation fields carry only `type` (all the FE needs); their Kysely DB defs live in
    // buildRequestsKyselyFilter (RELATION_DEFS).
    supplier: { type: 'relation' },
    workflowTitle: { type: 'relation' },
    tags: { type: 'relation' },
    // Core-data relations resolved off request_computed_properties FKs; DB defs (COMPUTED join columns)
    // live in buildRequestsKyselyFilter (RELATION_DEFS). department and costCenter both point at
    // `departments`; market points at custom_core_data_options.value.
    department: { type: 'relation' },
    costCenter: { type: 'relation' },
    market: { type: 'relation' },
    buyerLegalEntity: { type: 'relation' },
    paymentTerm: { type: 'relation' },
    paymentMethod: { type: 'relation' },
    // Computed fields: bare `type` (all the FE needs to pick operators); Kysely handlers live in
    // buildRequestsKyselyFilter (COMPUTED_DEFS). allCurrentAssignees is multi-valued → filter-only.
    // businessOwner/budgetHolder match a user's full name; category coalesces its two source tables.
    createdBy: { type: 'computed' },
    allCurrentAssignees: { type: 'computed' },
    businessOwner: { type: 'computed' },
    budgetHolder: { type: 'computed' },
    category: { type: 'computed' },
    // request_computed_properties columns, filtered as plain scalars via the 1:1 join in listRequestsQuery.
    // Keyed by the FE grid column field name, not the column.
    goLiveDate: { type: 'scalar', column: 'request_computed_properties.go_live_date', scalarType: 'date' },
    cancellationNotice: { type: 'scalar', column: 'request_computed_properties.cancellation_notice_days', scalarType: 'number' },
    note: { type: 'scalar', column: 'request_computed_properties.note', scalarType: 'string' },
    type: { type: 'scalar', column: 'request_computed_properties.type', scalarType: 'string' },
    contractStartDate: { type: 'scalar', column: 'request_computed_properties.contract_start_date', scalarType: 'date' },
    contractEndDate: { type: 'scalar', column: 'request_computed_properties.contract_end_date', scalarType: 'date' },
    // value/annualValue/negotiatedSavings store base currency but display converted to the org default
    // currency (`currency: true`), so the FE scales their filter thresholds back to base before sending.
    value: { type: 'scalar', column: 'request_computed_properties.value_number', scalarType: 'number', currency: true },
    annualValue: {
      type: 'scalar',
      column: 'request_computed_properties.annual_value_number',
      scalarType: 'number',
      currency: true,
    },
    // Only negotiatedSavings has a denormalized annualized column; costAvoidance/financeBudget do not,
    // so they display but must not be added here — there is no column to sort/filter them by.
    negotiatedSavings: {
      type: 'scalar',
      column: 'request_computed_properties.annual_negotiated_savings',
      scalarType: 'number',
      currency: true,
    },
    // Display-resolved scalars: the grid value is composed from several source tables, not one cp column, so
    // these carry no `column` — the backend supplies a resolving expression (RESOLVED_SCALAR_FILTER_DEFS in
    // resolvedRequestFieldExpressions). renewalDate/cancelBy resolve from the renewals record + agreement
    // term, not the form-derived cp columns.
    purchaseOrderNumber: { type: 'scalar', scalarType: 'string' },
    renewalDate: { type: 'scalar', scalarType: 'date' },
    cancelBy: { type: 'scalar', scalarType: 'date' },
  },
  sortableFields: {
    name: true,
    publicId: true,
    createdAt: true,
    completedAt: true,
    supplier: true,
    workflowTitle: true,
    createdBy: true,
    goLiveDate: true,
    cancellationNotice: true,
    note: true,
    // priority is a direct requests.priority enum; sorted via a CASE mirroring the client comparator
    // (buildRequestsKyselySorting).
    priority: true,
    // Computed-property scalars sort by their cp/requests column; relations and computed-name fields
    // sort by a correlated subquery (buildRequestsKyselySorting FIELD_MAPPINGS).
    type: true,
    contractStartDate: true,
    contractEndDate: true,
    value: true,
    annualValue: true,
    negotiatedSavings: true,
    // Display-resolved: sorted by the resolving expression in buildRequestsKyselySorting FIELD_MAPPINGS.
    purchaseOrderNumber: true,
    renewalDate: true,
    cancelBy: true,
    department: true,
    costCenter: true,
    market: true,
    buyerLegalEntity: true,
    paymentTerm: true,
    paymentMethod: true,
    businessOwner: true,
    budgetHolder: true,
    category: true,
  },
} as const

// Widened for the frontend's column capabilities.
export const REQUESTS_FILTER_CAPABILITIES: FilterCapabilityConfig = REQUESTS_FILTER_FIELDS
// Literal types preserved for the backend Kysely config.
export const REQUESTS_FILTER_FIELDS_DEF = REQUESTS_FILTER_FIELDS

export type RequestSortableField = keyof (typeof REQUESTS_FILTER_FIELDS_DEF)['sortableFields']

type RequestFilterableFields = (typeof REQUESTS_FILTER_FIELDS_DEF)['filterableFields']

type RequestFieldOfType<T extends RequestFilterableFields[keyof RequestFilterableFields]['type']> = {
  [K in keyof RequestFilterableFields]: RequestFilterableFields[K] extends { type: T } ? K : never
}[keyof RequestFilterableFields]

// The backend must supply a KyselyRelationDef for exactly the `type: 'relation'` keys.
export type RequestRelationField = RequestFieldOfType<'relation'>
// The backend must supply a handler for exactly the `type: 'computed'` keys.
export type RequestComputedField = RequestFieldOfType<'computed'>
